import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './ApproveOrderPage.module.css';
import { recipeApi, shopApi, orderApi } from '@xoxokiosk/shared';
import type { Recipe } from '@xoxokiosk/shared';
import QrScanner from 'qr-scanner';
import { detectPWA } from '../utils/pwaDetection';
import { PWAInstallPrompt } from '../components/PWAInstallPrompt';

type Step = 1 | 1.5 | 2 | 3;

// Helper function to determine glass type from backend data
const getGlassType = (order: any): string => {
  console.log('[DEBUG] getGlassType called for order:', {
    orderId: order.id,
    recipeId: order.recipeId,
    drinkName: order.name,
    hasRecipe: !!order.recipe,
    recipeGlass: order.recipe?.glass,
    glasswareParams: order.glasswareParams
  });

  // First try to get glass info from attached recipe object
  if (order.recipe && order.recipe.glass && order.recipe.glass.name) {
    console.log('[DEBUG] Using glass from recipe:', order.recipe.glass.name);
    return order.recipe.glass.name;
  }

  // Then try to get glass info from backend glasswareParams
  if (order.glasswareParams && order.glasswareParams.glassType) {
    console.log('[DEBUG] Using glass from glasswareParams.glassType:', order.glasswareParams.glassType);
    return order.glasswareParams.glassType;
  }
  if (order.glasswareParams && order.glasswareParams.name) {
    console.log('[DEBUG] Using glass from glasswareParams.name:', order.glasswareParams.name);
    return order.glasswareParams.name;
  }

  // Fallback to guessing based on drink name if no backend data
  const name = order.name.toLowerCase();
  if (name.includes('martini') || name.includes('cosmopolitan') || name.includes('margarita')) {
    console.log('[DEBUG] Guessing glass type: Martini Glass (based on drink name)');
    return 'Martini Glass';
  }
  if (name.includes('wine') || name.includes('sangria')) {
    console.log('[DEBUG] Guessing glass type: Wine Glass (based on drink name)');
    return 'Wine Glass';
  }
  if (name.includes('beer') || name.includes('ale') || name.includes('lager')) {
    console.log('[DEBUG] Guessing glass type: Beer Glass (based on drink name)');
    return 'Beer Glass';
  }
  if (name.includes('shot') || name.includes('tequila') || name.includes('whiskey')) {
    console.log('[DEBUG] Guessing glass type: Shot Glass (based on drink name)');
    return 'Shot Glass';
  }
  if (name.includes('coffee') || name.includes('espresso') || name.includes('latte')) {
    console.log('[DEBUG] Guessing glass type: Coffee Mug (based on drink name)');
    return 'Coffee Mug';
  }
  // Default to highball glass for cocktails and mixed drinks
  console.log('[DEBUG] Defaulting to glass type: Highball Glass');
  return 'Highball Glass';
};

const ApproveOrderPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const shopId = searchParams.get('shopId');
  const [orders, setOrders] = useState<any[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [approving, setApproving] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [fetchingFromBackend, setFetchingFromBackend] = useState(false);
  const [showPWAPrompt, setShowPWAPrompt] = useState(false);
  const [pwaDismissed, setPwaDismissed] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const qrScannerRef = useRef<QrScanner | null>(null);

// Generate or retrieve device identifier for this machine
  const getDeviceIdentifier = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  };

  // Generate or retrieve user session identifier
  const getUserSessionId = () => {
    let sessionId = sessionStorage.getItem('userSessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('userSessionId', sessionId);
      // Store session start time
      sessionStorage.setItem('sessionStartTime', Date.now().toString());
    }
    return sessionId;
  };

  // Fetch orders from backend as fallback
  const fetchOrdersFromBackend = async (shopId: string) => {
    try {
      console.log('[DEBUG] Attempting to fetch orders from backend for shopId:', shopId);
      setFetchingFromBackend(true);
      
      const deviceId = getDeviceIdentifier();
      const userSessionId = getUserSessionId();
      
      // Try to fetch orders for this user session first
      const response = await shopApi.getOrdersForShop(shopId, { 
        kioskInstanceCode: deviceId,
        userSessionId: userSessionId 
      });
      
      if (response.data && response.data.length > 0) {
        console.log('[DEBUG] Found user-specific orders in backend:', response.data.length);
        
        // Store fetched orders in localStorage for future use
        const existingOrders = JSON.parse(localStorage.getItem('pendingOrders') || '[]');
        const updatedOrders = [...existingOrders];
        
        // Add backend orders that aren't already in localStorage
        response.data.forEach((backendOrder: any) => {
          const exists = updatedOrders.find((order: any) => order.id === backendOrder.id);
          if (!exists) {
            updatedOrders.push(backendOrder);
          }
        });
        
        localStorage.setItem('pendingOrders', JSON.stringify(updatedOrders));
        return response.data;
      }
      
      console.log('[DEBUG] No user-specific orders found, trying device orders...');
      
      // Fallback to device-only orders if user session has no orders
      const deviceResponse = await shopApi.getOrdersForShop(shopId, { 
        kioskInstanceCode: deviceId 
      });
      
      if (deviceResponse.data && deviceResponse.data.length > 0) {
        console.log('[DEBUG] Found device orders in backend:', deviceResponse.data.length);
        
        // Store fetched orders in localStorage
        const existingOrders = JSON.parse(localStorage.getItem('pendingOrders') || '[]');
        const updatedOrders = [...existingOrders];
        
        deviceResponse.data.forEach((backendOrder: any) => {
          const exists = updatedOrders.find((order: any) => order.id === backendOrder.id);
          if (!exists) {
            updatedOrders.push(backendOrder);
          }
        });
        
        localStorage.setItem('pendingOrders', JSON.stringify(updatedOrders));
        return deviceResponse.data;
      }
      
      console.log('[DEBUG] No orders found in backend');
      return [];
    } catch (error) {
      console.error('[ERROR] Failed to fetch orders from backend:', error);
      // Don't show error to user for backend fetch failures, just log and continue
      return [];
    } finally {
      setFetchingFromBackend(false);
    }
  };

  console.log('[DEBUG] ApproveOrderPage component mounted/updated:', {
    shopId,
    currentStep,
    ordersCount: orders.length,
    selectedOrder: selectedOrder ? {
      id: selectedOrder.id,
      name: selectedOrder.name,
      recipeId: selectedOrder.recipeId,
      hasRecipe: !!selectedOrder.recipe
    } : null,
    loading,
    error,
    showQRScanner
  });

  useEffect(() => {
    const loadOrders = async () => {
      if (!shopId) {
        setShowQRScanner(true);
        setLoading(false);
        return;
      }

      // First, try to get orders from localStorage
      const storedOrders = JSON.parse(localStorage.getItem('pendingOrders') || '[]');
      let shopOrders = storedOrders.filter((order: any) => order.shopId === shopId);

      console.log('[DEBUG] ApproveOrderPage - loaded orders from localStorage:', {
        all: storedOrders,
        shopId,
        filtered: shopOrders
      });

      // If no orders found in localStorage, try fetching from backend
      if (shopOrders.length === 0) {
        console.log('[DEBUG] No orders in localStorage, trying backend fallback...');
        const backendOrders = await fetchOrdersFromBackend(shopId);
        shopOrders = backendOrders.filter((order: any) => order.shopId === shopId);
        
        console.log('[DEBUG] Orders after backend fallback:', {
          backendOrders: backendOrders.length,
          filtered: shopOrders.length
        });
      }

      if (shopOrders.length === 0) {
        setError('No pending orders found for this machine. Please submit an order first.');
        setLoading(false);
        return;
      }

      // Fetch full recipe details for each order
      let recipeIds: string[] = [];
      try {
        recipeIds = shopOrders
          .map((order: any) => order.recipeId)
          .filter((id: string) => id); // Filter out any orders without recipeId

        if (recipeIds.length > 0) {
          console.log('[DEBUG] Fetching recipes for IDs:', recipeIds);
          const recipeResponse = await recipeApi.getRecipesByIds(recipeIds);
          console.log('[DEBUG] Recipe API Response:', {
            status: recipeResponse.status,
            statusText: recipeResponse.statusText,
            headers: recipeResponse.headers,
            config: recipeResponse.config
          });
          
          const recipes = recipeResponse.data;
          console.log('[DEBUG] Fetched Recipes Array:', recipes);
          console.log('[DEBUG] Number of recipes fetched:', recipes.length);
          
          // Log each recipe individually with full details
          recipes.forEach((recipe: Recipe, index: number) => {
            console.log(`[DEBUG] Recipe ${index + 1}:`, {
              id: recipe.id,
              name: recipe.name,
              description: recipe.description,
              ice: recipe.ice,
              mixingMethod: recipe.mixingMethod,
              hidden: recipe.hidden,
              glassId: recipe.glassId,
              glass: recipe.glass,
              garnishId: recipe.garnishId,
              garnish: recipe.garnish,
              image: recipe.image,
              type: recipe.type,
              customised: recipe.customised,
              shopId: recipe.shopId,
              userId: recipe.userId,
              drinkFeatures: recipe.drinkFeatures,
              recipeIngredient: recipe.recipeIngredient,
              recipeImage: recipe.recipeImage,
              ABV: recipe.ABV,
              layeringSequence: recipe.layeringSequence
            });
          });

          // Create a map of recipeId to recipe for easy lookup
          const recipeMap = new Map<string, Recipe>();
          recipes.forEach((recipe: Recipe) => {
            recipeMap.set(recipe.id, recipe);
          });
          
          console.log('[DEBUG] Recipe Map created:', {
            size: recipeMap.size,
            keys: Array.from(recipeMap.keys()),
            entries: Array.from(recipeMap.entries()).map(([key, recipe]) => ({
              recipeId: key,
              recipeName: recipe.name,
              glassName: recipe.glass?.name
            }))
          });

          // Attach full recipe data to orders
          const ordersWithRecipes = shopOrders.map((order: any) => ({
            ...order,
            recipe: recipeMap.get(order.recipeId) || null
          }));

          console.log('[DEBUG] Orders with recipes attached:', ordersWithRecipes);
          console.log('[DEBUG] Orders summary:', ordersWithRecipes.map((order: any) => ({
            orderId: order.id,
            recipeId: order.recipeId,
            drinkName: order.name,
            hasRecipe: !!order.recipe,
            glassName: order.recipe?.glass?.name,
            glasswareParams: order.glasswareParams
          })));
          
          setOrders(ordersWithRecipes);
        } else {
          console.log('[DEBUG] No recipe IDs found in orders, skipping recipe fetch');
          // Fallback to orders without recipes if no recipeIds
          setOrders(shopOrders);
        }
      } catch (recipeError) {
        console.error('[ERROR] Failed to fetch recipes:', {
          error: recipeError,
          message: recipeError instanceof Error ? recipeError.message : 'Unknown error',
          stack: recipeError instanceof Error ? recipeError.stack : undefined,
          recipeIds: recipeIds
        });
        // Fallback to orders without recipes
        console.log('[DEBUG] Falling back to orders without recipe data:', shopOrders);
        setOrders(shopOrders);
      }

      setLoading(false);
    };

    if (shopId) {
      loadOrders();
    } else {
      // No shopId provided, show QR scanner
      setShowQRScanner(true);
      setLoading(false);
    }
  }, [shopId]);

  // Manage QR scanner lifecycle
  useEffect(() => {
    if (showQRScanner && videoRef.current && !scannerActive) {
      startQRScanner();
    }

    return () => {
      stopQRScanner();
    };
  }, [showQRScanner, scannerActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopQRScanner();
    };
  }, []);

  // Detect PWA mode for persistent storage
  useEffect(() => {
    const pwaStatus = detectPWA();
    const dismissed = sessionStorage.getItem('pwaPromptDismissed') === 'true';
    
    if (!pwaStatus.isPWA && !dismissed) {
      setShowPWAPrompt(true);
    }
  }, []);

  const dismissPWAPrompt = () => {
    setShowPWAPrompt(false);
    setPwaDismissed(true);
    sessionStorage.setItem('pwaPromptDismissed', 'true');
  };

  const handleOrderSelect = (order: any) => {
    console.log('[DEBUG] Order selected:', {
      orderId: order.id,
      recipeId: order.recipeId,
      drinkName: order.name,
      hasRecipe: !!order.recipe,
      recipeData: order.recipe,
      glassFromRecipe: order.recipe?.glass?.name,
      glasswareParams: order.glasswareParams
    });
    
    setSelectedOrder(order);
    setCurrentStep(1.5); // Go to preparation step first
  };

  const handlePreparationReady = () => {
    setCurrentStep(2);
    // Auto-approve after a short delay
    setTimeout(() => {
      handleApprove(selectedOrder);
    }, 1000);
  };

  // Use pre-calculated brewing time from order object
  const getBrewingTime = (order: any): number => {
    return order.brewingTime || 3000; // Default 3 seconds if not provided
  };

  const handleApprove = async (order: any) => {
    setApproving(true);
    try {
      console.log("[DEBUG] Approving order:", order.orderId, "for shop:", order.shopId);
      
      // Use pre-calculated brewing time from order object
      const brewingTime = getBrewingTime(order);
      console.log(`[DEBUG] Using stored brewing time: ${brewingTime}ms (${(brewingTime/1000).toFixed(1)}s)`);
      
      // Call the kiosk backend to approve the order (which will call XOXO API)
      await orderApi.approveOrder(order.orderId, order.shopId);

      // Set CSS variable for animation duration
      const animationDuration = brewingTime / 1000; // Convert to seconds
      document.documentElement.style.setProperty('--brewing-duration', `${animationDuration}s`);
      
      setTimeout(() => {
        // Remove completed order
        const updatedOrders = orders.filter(o => o.id !== order.orderId);
        setOrders(updatedOrders);
        localStorage.setItem('pendingOrders', JSON.stringify(
          JSON.parse(localStorage.getItem('pendingOrders') || '[]').filter((o: any) => o.id !== order.id)
        ));

        // Always go to complete step after brewing
        setCurrentStep(3);
        setApproving(false);
        
        // Reset CSS variable
        document.documentElement.style.removeProperty('--brewing-duration');
      }, brewingTime); // Use dynamic brewing time
    } catch (err) {
      console.error('Error approving order:', err);
      alert('Failed to approve order.');
      setApproving(false);
    }
  };

  const handleQRScan = (scannedData: string) => {
    try {
      // Try to parse as URL with shopId parameter
      const url = new URL(scannedData);
      const scannedShopId = url.searchParams.get('shopId');
      
      if (scannedShopId) {
        // Stop the scanner first
        stopQRScanner();
        // Navigate to the same page with the shopId
        window.location.href = `/approve?shopId=${scannedShopId}`;
      } else {
        setScanError('Invalid QR code. Please scan a valid machine QR code.');
      }
    } catch (err) {
      setScanError('Invalid QR code format. Please scan a valid machine QR code.');
    }
  };

  const startQRScanner = async () => {
    if (!videoRef.current) return;

    try {
      setScanError(null);
      setScannerActive(true);

      // Check if we're on HTTPS (required for camera access in production)
      // Allow HTTP localhost for development with camera access
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname.startsWith('127.');
      const isHttps = window.location.protocol === 'https:';
      
      if (!isHttps && !isLocalhost) {
        throw new Error('Camera access requires HTTPS. Please ensure you\'re accessing the site over a secure connection.');
      }

      // Check if camera is available
      const hasCamera = await QrScanner.hasCamera();
      if (!hasCamera) {
        throw new Error('No camera found on this device');
      }

      // Create QR scanner instance
      qrScannerRef.current = new QrScanner(
        videoRef.current,
        (result) => {
          console.log('QR Code detected:', result.data);
          handleQRScan(result.data);
        },
        {
          onDecodeError: (err) => {
            // Only log decode errors, don't show to user as they're common during scanning
            console.log('QR decode error:', err);
          },
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment', // Use back camera on mobile
        }
      );

      // Start scanning
      await qrScannerRef.current.start();
      console.log('QR Scanner started successfully');
    } catch (err) {
      console.error('Failed to start QR scanner:', err);
      let errorMessage = 'Failed to access camera.';
      
      if (err instanceof Error) {
        if (err.message.includes('NotAllowedError') || err.message.includes('Permission denied')) {
          errorMessage = 'Camera permission denied. Please allow camera access and try again.';
        } else if (err.message.includes('NotFoundError') || err.message.includes('No camera')) {
          errorMessage = 'No camera found on this device.';
        } else if (err.message.includes('NotReadableError')) {
          errorMessage = 'Camera is being used by another application.';
        } else if (err.message.includes('HTTPS')) {
          errorMessage = 'Camera access requires HTTPS. Please ensure you\'re accessing the site over a secure connection.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setScanError(errorMessage);
      setScannerActive(false);
    }
  };

  const stopQRScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.stop();
      qrScannerRef.current.destroy();
      qrScannerRef.current = null;
    }
    setScannerActive(false);
    setScanError(null);
  };

  const handleManualEntry = () => {
    const shopId = prompt('Enter Machine ID:');
    if (shopId && shopId.trim()) {
      window.location.href = `/approve?shopId=${shopId.trim()}`;
    }
  };

  const handleDone = () => {
    // This is now handled in the JSX for Step 3
    window.location.href = '/kiosk/live';
  };

  if (loading || fetchingFromBackend) return (
    <div className={styles.container}>
      <div className={styles.loading}>
        <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
        {fetchingFromBackend ? 'Checking for pending orders...' : 'Loading your order...'}
      </div>
    </div>
  );

  if (error) return (
    <div className={styles.container}>
      <div className={styles.error}>
        <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
        <h2>Oops!</h2>
        <p>{error}</p>
        <button className={styles.errorBtn} onClick={() => window.location.href = '/kiosk/live'}>
          Join New Experience
        </button>
      </div>
    </div>
  );

  if (showQRScanner) return (
    <div className={styles.container}>
      <div className={styles.qrScanner}>
        <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
        <h1>Scan Machine QR Code</h1>
        <p>Scan the QR code on your XOXO Beverages machine to start approving orders.</p>
        
        <div className={styles.scannerArea}>
          {scannerActive ? (
            <div className={styles.videoContainer}>
              <video
                ref={videoRef}
                className={styles.scannerVideo}
                playsInline
                muted
                autoPlay
              />
              <div className={styles.scanOverlay}>
                <div className={styles.scanFrame}></div>
                <p className={styles.scanInstruction}>Position QR code within the frame</p>
              </div>
            </div>
          ) : (
            <div className={styles.scannerPlaceholder}>
              <div className={styles.scannerIcon}>📱</div>
              <p>Camera access required</p>
              <small>Position QR code within the frame</small>
              <small style={{marginTop: '0.5rem', opacity: 0.7}}>Make sure to allow camera permissions</small>
              {scanError && (
                <div className={styles.scanError}>
                  <small style={{color: '#ef4444', marginTop: '0.5rem'}}>{scanError}</small>
                  <button 
                    className={styles.retryBtn}
                    onClick={() => startQRScanner()}
                    style={{marginTop: '0.5rem'}}
                  >
                    🔄 Try Again
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className={styles.scannerActions}>
          <button className={styles.manualEntryBtn} onClick={handleManualEntry}>
            🔢 Enter Machine ID Manually
          </button>
          <button className={styles.cancelBtn} onClick={() => window.location.href = '/kiosk/live'}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      {/* PWA Install Prompt */}
      {showPWAPrompt && (
        <PWAInstallPrompt 
          onDismiss={dismissPWAPrompt}
          onInstall={() => {
            dismissPWAPrompt();
            window.location.reload();
          }}
        />
      )}
      
      {currentStep === 1 && (
        <div className={styles.step}>
          <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
          <div className={styles.greeting}>
            <h1>Hey there!</h1>
            <p>Choose a drink to make:</p>
          </div>
          <div className={styles.orderList}>
            {orders.map((order, index) => (
              <div key={order.id} className={`${styles.orderItem} ${styles.orderItemWithImage}`}>
                {/* Drink Image */}
                {(() => {
                  const imageUrl = order.recipe?.recipeImage?.imageUrl || order.recipe?.image;
                  const hasImage = !!imageUrl;
                  return hasImage ? (
                    <img
                      src={imageUrl}
                      alt={order.name}
                      className={styles.drinkImage}
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        const src = img.src || '';
                        if (src && !src.includes('/api/uploads/proxy')) {
                          img.src = `/api/uploads/proxy?url=${encodeURIComponent(src)}`;
                        } else {
                          img.style.display = 'none';
                        }
                      }}
                    />
                  ) : (
                    <div className={styles.imagePlaceholder}>
                      No Image
                    </div>
                  );
                })()}
                
                <div className={styles.orderItemContent}>
                  <div className={styles.orderInfo}>
                    <h3>{order.name}</h3>
                    <p>Ordered: {new Date(order.timestamp).toLocaleTimeString()}</p>
                  </div>
                  <button 
                    className={styles.selectBtn} 
                    onClick={() => handleOrderSelect(order)}
                  >
                    Make This
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {currentStep === 1.5 && selectedOrder && (
        <div className={styles.step}>
          {(() => {
            const glassType = getGlassType(selectedOrder);
            console.log('[DEBUG] Rendering preparation step:', {
              selectedOrderId: selectedOrder.id,
              selectedOrderName: selectedOrder.name,
              determinedGlassType: glassType,
              recipeData: selectedOrder.recipe,
              glassFromRecipe: selectedOrder.recipe?.glass?.name,
              glasswareParams: selectedOrder.glasswareParams
            });
            return null;
          })()}
          <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
          <div className={styles.preparation}>
            <h1>Prep your glass for <span className={styles.drinkNameHighlight}>{selectedOrder.name}</span></h1>
            
            {/* Drink Image and Glass Info Row */}
            <div className={styles.drinkGlassRow}>
              {/* Drink Image */}
              <div className={styles.drinkImageContainer}>
                {(() => {
                  const imageUrl = selectedOrder.recipe?.recipeImage?.imageUrl || selectedOrder.recipe?.image;
                  const hasImage = !!imageUrl;
                  return hasImage ? (
                    <img
                      src={imageUrl}
                      alt={selectedOrder.name}
                      className={styles.drinkImageMedium}
                      onError={(e) => {
                        const img = e.currentTarget as HTMLImageElement;
                        const src = img.src || '';
                        if (src && !src.includes('/api/uploads/proxy')) {
                          img.src = `/api/uploads/proxy?url=${encodeURIComponent(src)}`;
                        } else {
                          img.style.display = 'none';
                        }
                      }}
                    />
                  ) : (
                    <div className={styles.imagePlaceholderMedium}>
                      No Image
                    </div>
                  );
                })()}
              </div>
              
              {/* Glass Info */}
              <div className={styles.glassInfo}>
                <div className={styles.glassIcon}>🥂</div>
                <h2>{getGlassType(selectedOrder)}</h2>
              </div>
            </div>
            <div className={styles.instructions}>
              <p>📋 <strong>Follow these steps carefully:</strong></p>
              <ol>
                <li>Take a <strong>{getGlassType(selectedOrder).toLowerCase()}</strong> from the shelf</li>
                <li>Fill the glass about ¾ full with ice cubes</li>
                <li style={{background: 'rgba(255, 110, 168, 0.1)', padding: '0.5rem', borderRadius: '6px', border: '1px solid rgba(255, 110, 168, 0.2)'}}>
                  <strong>⚠️ CRITICAL:</strong> Place the glass on the <strong style={{color: 'var(--cocktail-pink)'}}>dispensing base</strong> below the dispenser nozzle
                </li>
              </ol>
              <div className={styles.instructionWarning}>
                <p>🔴 <strong>Glass must be in place before brewing starts!</strong></p>
                <p>Make sure your glass is properly positioned under the dispenser.</p>
              </div>
            </div>
            <button className={styles.readyBtn} onClick={handlePreparationReady}>
              ✅ Glass is Ready - Start Brewing!
            </button>
          </div>
        </div>
      )}

      {currentStep === 2 && selectedOrder && (
        <div className={styles.step}>
          <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
          <div className={styles.brewing}>
            <h1>Now Brewing...</h1>
            
            {/* Drink Image */}
            {(() => {
              const imageUrl = selectedOrder.recipe?.recipeImage?.imageUrl || selectedOrder.recipe?.image;
              const hasImage = !!imageUrl;
              return hasImage ? (
                <img
                  src={imageUrl}
                  alt={selectedOrder.name}
                  className={styles.drinkImageLarge}
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    const src = img.src || '';
                    if (src && !src.includes('/api/uploads/proxy')) {
                      img.src = `/api/uploads/proxy?url=${encodeURIComponent(src)}`;
                    } else {
                      img.style.display = 'none';
                    }
                  }}
                />
              ) : (
                <div className={styles.imagePlaceholderLarge}>
                  No Image
                </div>
              );
            })()}
            
            <p><strong>{selectedOrder.name}</strong></p>
            <div className={styles.animation}>
              <div className={styles.cup}>
                <div className={styles.liquid}></div>
              </div>
            </div>
            <p>Your drink is being prepared!</p>
          </div>
          <button className={styles.doneBtn} onClick={handleDone}>
            DONE?
          </button>
        </div>
      )}

      {currentStep === 3 && (
        <div className={styles.step}>
          <img src="/company-logo.svg" alt="XOXO Beverages" className={styles.logo} />
          <div className={styles.final}>
            <h1>Cheers! 🎉</h1>
            
            {/* Drink Image for Garnish Reference */}
            {selectedOrder && (() => {
              const imageUrl = selectedOrder.recipe?.recipeImage?.imageUrl || selectedOrder.recipe?.image;
              const hasImage = !!imageUrl;
              return hasImage ? (
                <img
                  src={imageUrl}
                  alt={selectedOrder.name}
                  className={styles.drinkImageLarge}
                  onError={(e) => {
                    const img = e.currentTarget as HTMLImageElement;
                    const src = img.src || '';
                    if (src && !src.includes('/api/uploads/proxy')) {
                      img.src = `/api/uploads/proxy?url=${encodeURIComponent(src)}`;
                    } else {
                      img.style.display = 'none';
                    }
                  }}
                />
              ) : (
                <div className={styles.imagePlaceholderLarge}>
                  No Image
                </div>
              );
            })()}
            
            <p>Add your garnish and enjoy your <span className={styles.drinkNameHighlight}>{selectedOrder?.name}</span>.</p>
            <p>Don't forget to tag <a href="instagram://story-camera" className={styles.instagramLink}>@xoxobeverages</a> on Instagram!</p>
            {orders.length > 0 && (
              <div className={styles.finalActions}>
                <button className={styles.makeAnotherBtn} onClick={() => setCurrentStep(1)}>
                  Make Another
                </button>
                <button className={styles.joinNewBtn} onClick={() => window.location.href = '/kiosk/live'}>
                  Join New Experience
                </button>
              </div>
            )}
            {orders.length === 0 && (
              <button className={styles.joinNewBtn} onClick={() => window.location.href = '/kiosk/live'}>
                Join New Experience
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproveOrderPage;
